import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { DataService } from '@app-core/services/data/data.service';
import { DateService } from '@app-core/services/date/date.service';
import { EXPORT_HISTORY_TABLE_COLUMN, DOWNLOADS_DURATION_LIST, DownloadStatus, ReportType } from '@app-reports/constants/reports.constants';
import { AsyncReportRow, AsyncReportsParams, AsyncReportsResponse, ExportHistoryRouteParams } from '@app-reports/models/reports.model';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { Subject } from 'rxjs';
import { finalize, skip, takeUntil, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { ReportTypeKey } from '@app-core/models/core.model';
import { CAMERA_EVENT_LABLES, HEALTH_EVENT_LABLES } from '@app-assets/constants/assets.constants';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';

@Component({
  selector: 'app-export-history',
  templateUrl: './export-history.component.html',
  styleUrls: ['./export-history.component.scss'],
})
export class ExportHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('paginator', { static: true }) public paginator: MatPaginator;

  public Status = DownloadStatus;
  public reportTypes;
  public durationList = DOWNLOADS_DURATION_LIST;
  public durationDays: number = 30;
  private startDate: string;
  private endDate: string;

  public displayedColumns: string[] = EXPORT_HISTORY_TABLE_COLUMN;
  public pageSize: number = 10;
  private skip = 0;
  public logsCount: number = 0;
  private fleetId: string;
  public tableSource: MatTableDataSource<any> = new MatTableDataSource([]);
  public loader: boolean = false;
  private HEALTH_EVENT_LABLES_LIST = HEALTH_EVENT_LABLES;
  private CAMERA_EVENT_LABLES_LIST = CAMERA_EVENT_LABLES;

  public reportTypeControl = new FormControl<string | null>(null);

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private downloadsService: ReportsService,
    private dataService: DataService,
    private dateService: DateService,
    private route: ActivatedRoute,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit(): void {
    this.reportTypes = this.downloadsService.getReportTypes(Object.values(ReportType));
    ({ startDate: this.startDate, endDate: this.endDate } = this.getStartAndEndTime(this.durationDays));

    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params: ExportHistoryRouteParams) => {
      if (params?.reportType) {
        this.reportTypeControl.setValue(params.reportType);
      }
    });
    this.dataService._currentFleet
      .pipe(
        takeUntil(this.ngUnsubscribe),
        tap((value) => {
          if (value) {
            this.fleetId = value;
            this.getExportHistory();
          }
        }),
        skip(1),
        tap(() => {
          this.clearSearchOnFleetChange();
        })
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.paginator.page.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: PageEvent) => {
      this.skip = event.pageIndex * this.pageSize;
      this.getExportHistory();
    });
    this.getExportHistory();
  }

  private getStartAndEndTime(noOfDays) {
    const { from, to } = this.dateService.getDateRange(noOfDays);
    return {
      startDate: this.dateService.toDaysStartISO(from),
      endDate: this.dateService.toDaysEndISOPlusOne(to),
    };
  }

  public durationChange(event: any): void {
    const days = event.value;
    ({ startDate: this.startDate, endDate: this.endDate } = this.getStartAndEndTime(days));
    this.mixpanelService.track('Select Duration in Export History / Reports Page', {
      'Days Selected': days,
      'Start Date': this.startDate,
      'End Date': this.endDate,
    });
    this.getExportHistory();
  }

  public reportTypeChange(): void {
    const reportType = this.reportTypeControl.value;
    this.mixpanelService.track('Select Request Type - Export history / Reports Page', {
      'Report Type': reportType,
    });
    this.getExportHistory();
  }

  private clearSearchOnFleetChange(): void {
    this.reportTypeControl.setValue(''); // Clear the search input
    this.durationDays = 30;
    this.durationChange({ value: this.durationDays });
    this.skip = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  public openFAQ() {
    this.dataService?.openFaq('ExportAsyncDownloadHistory');
  }

  public getExportHistory() {
    this.loader = true;
    this.tableSource.data = new Array(this.pageSize).fill(undefined);
    const reportType = this.reportTypeControl.value ?? null;
    const params: AsyncReportsParams = {
      startDate: this.startDate,
      endDate: this.endDate,
      limit: this.pageSize,
      offset: this.skip,
      ...(this.fleetId ? { fleetId: this.fleetId } : {}),
      ...(reportType ? { reportType: reportType } : {}),
    };
    this.downloadsService
      .getAsyncReportsList(params)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: AsyncReportsResponse) => {
          let data = res?.rows || [];
          this.addReportName(data);
          this.tableSource.data = data;
          this.logsCount = res?.totalCount || 0;
        },
        () => {
          this.tableSource.data = [];
          this.logsCount = 0;
        }
      );
  }

  public downloadReport(row: AsyncReportRow) {
    const downloadUrl: string = row?.downloadUrl || '';
    this.mixpanelService.track('Download Report', {
      'Report Type': row?.reportType,
    });
    this.downloadsService.downloadFiles(downloadUrl);
  }

  public addReportName(rows: AsyncReportRow[]) {
    rows.forEach((row) => (row['reportName'] = this.getReportName(row.reportType, row.payload?.eventType)));
  }

  public getReportName(reportTypeKey: ReportTypeKey, eventType: string) {
    let label;
    switch (reportTypeKey) {
      case 'FLEET_CAMERA_EVENTS':
        if (eventType) {
          label = this.CAMERA_EVENT_LABLES_LIST[eventType]?.label || eventType;
        } else {
          label = 'All Camera Visibility Events';
        }
        break;
      case 'FLEET_HEALTH_EVENTS':
        label = this.HEALTH_EVENT_LABLES_LIST[eventType]?.label || eventType;
        break;
      case 'FLEET_SUPRESSED_DIAGNOSTICS_EVENTS':
        if (eventType) {
          label = 'Muted ' + this.CAMERA_EVENT_LABLES_LIST[eventType]?.label || eventType;
        } else {
          label = 'Muted Camera Visibility Events';
        }
        break;
      default:
        label = Object.values(ReportType).find((val) => val.key === reportTypeKey)?.label || '-';
        break;
    }
    return label;
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
